export interface ElementOption {
  readonly atomicNumber: number;
  readonly symbol: string;
  readonly name: string;
}

export const EDITOR_ELEMENTS: readonly ElementOption[] = [
  { atomicNumber: 6, symbol: 'C', name: 'Carbon' },
  { atomicNumber: 7, symbol: 'N', name: 'Nitrogen' },
  { atomicNumber: 8, symbol: 'O', name: 'Oxygen' },
  { atomicNumber: 9, symbol: 'F', name: 'Fluorine' },
  { atomicNumber: 15, symbol: 'P', name: 'Phosphorus' },
  { atomicNumber: 16, symbol: 'S', name: 'Sulfur' },
  { atomicNumber: 17, symbol: 'Cl', name: 'Chlorine' },
  { atomicNumber: 33, symbol: 'As', name: 'Arsenic' },
  { atomicNumber: 34, symbol: 'Se', name: 'Selenium' },
  { atomicNumber: 35, symbol: 'Br', name: 'Bromine' },
  { atomicNumber: 53, symbol: 'I', name: 'Iodine' }
];

const ELEMENT_SYMBOLS = [
  '',
  'H',
  'He',
  'Li',
  'Be',
  'B',
  'C',
  'N',
  'O',
  'F',
  'Ne',
  'Na',
  'Mg',
  'Al',
  'Si',
  'P',
  'S',
  'Cl',
  'Ar',
  'K',
  'Ca',
  'Sc',
  'Ti',
  'V',
  'Cr',
  'Mn',
  'Fe',
  'Co',
  'Ni',
  'Cu',
  'Zn',
  'Ga',
  'Ge',
  'As',
  'Se',
  'Br',
  'Kr',
  'Rb',
  'Sr',
  'Y',
  'Zr',
  'Nb',
  'Mo',
  'Tc',
  'Ru',
  'Rh',
  'Pd',
  'Ag',
  'Cd',
  'In',
  'Sn',
  'Sb',
  'Te',
  'I',
  'Xe',
  'Cs',
  'Ba',
  'La',
  'Ce',
  'Pr',
  'Nd',
  'Pm',
  'Sm',
  'Eu',
  'Gd',
  'Tb',
  'Dy',
  'Ho',
  'Er',
  'Tm',
  'Yb',
  'Lu',
  'Hf',
  'Ta',
  'W',
  'Re',
  'Os',
  'Ir',
  'Pt',
  'Au',
  'Hg',
  'Tl',
  'Pb',
  'Bi',
  'Po',
  'At',
  'Rn',
  'Fr',
  'Ra',
  'Ac',
  'Th',
  'Pa',
  'U',
  'Np',
  'Pu',
  'Am',
  'Cm',
  'Bk',
  'Cf',
  'Es',
  'Fm',
  'Md',
  'No',
  'Lr',
  'Rf',
  'Db',
  'Sg',
  'Bh',
  'Hs',
  'Mt',
  'Ds',
  'Rg',
  'Cn',
  'Nh',
  'Fl',
  'Mc',
  'Lv',
  'Ts',
  'Og'
] as const;

export function elementSymbol(atomicNumber: number): string {
  return ELEMENT_SYMBOLS[atomicNumber] ?? `[${atomicNumber}]`;
}

const SUPERSCRIPT_DIGITS: Readonly<Record<string, string>> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹'
};

export function formalChargeSuffix(formalCharge: number): string {
  if (formalCharge === 0) return '';
  const magnitude = Math.abs(formalCharge);
  const digits =
    magnitude === 1
      ? ''
      : String(magnitude)
          .split('')
          .map((digit) => SUPERSCRIPT_DIGITS[digit])
          .join('');
  return digits + (formalCharge > 0 ? '⁺' : '⁻');
}

export function formalChargeLabel(formalCharge: number): string {
  if (formalCharge === 0) return '';
  const magnitude = Math.abs(formalCharge);
  return (magnitude === 1 ? '' : String(magnitude)) + (formalCharge > 0 ? '+' : '−');
}

export function formalChargeLabelOffsetX(atomicNumber: number): number {
  return elementSymbol(atomicNumber).length > 1 ? 12 : 9;
}

const RESONANCE_CHARGE_FRACTION_EPSILON = 1e-6;
const RESONANCE_CHARGE_MAX_DENOMINATOR = 6;

/**
 * Formats a resonance-charge display override (ADR 0003) as a leading-sign reduced fraction
 * (e.g. `−1/2`, `−2/3`) - the opposite sign placement from `formalChargeLabel`'s trailing-sign
 * integer convention, both matching textbook resonance notation and visually distinguishing a
 * display override from an ordinary formal charge. Falls back to a rounded decimal for values
 * that aren't a clean fraction with a small denominator.
 */
export function resonanceChargeLabel(resonanceCharge: number): string {
  if (resonanceCharge === 0) return '0';
  const sign = resonanceCharge > 0 ? '+' : '−';
  const magnitude = Math.abs(resonanceCharge);
  for (let denominator = 1; denominator <= RESONANCE_CHARGE_MAX_DENOMINATOR; denominator++) {
    const numerator = Math.round(magnitude * denominator);
    if (Math.abs(magnitude - numerator / denominator) < RESONANCE_CHARGE_FRACTION_EPSILON) {
      return denominator === 1 ? `${sign}${numerator}` : `${sign}${numerator}/${denominator}`;
    }
  }
  return `${sign}${Math.round(magnitude * 100) / 100}`;
}

export function isotopeSuperscript(isotope: number | undefined): string {
  if (isotope === undefined) return '';
  return String(isotope)
    .split('')
    .map((digit) => SUPERSCRIPT_DIGITS[digit])
    .join('');
}

export function radicalLabel(radicalElectronCount: number | undefined): string {
  if (!radicalElectronCount) return '';
  return '•'.repeat(Math.min(radicalElectronCount, 3));
}

export function implicitHydrogenLabel(count: number): string {
  if (count <= 1) return 'H';
  // Any count: a lone carbon carries four (CH4, #1068).
  return `H${[...String(count)].map((digit) => '₀₁₂₃₄₅₆₇₈₉'[Number(digit)]).join('')}`;
}

export function atomDisplayLabel(atomicNumber: number, formalCharge: number): string {
  return elementSymbol(atomicNumber) + formalChargeSuffix(formalCharge);
}
